import { useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faPen } from '@fortawesome/pro-regular-svg-icons';
import type { MissingTimesheet } from '@core/timesheetApproval/missingTimesheet.types';
import { minutesToMilliseconds } from '@core/util/datetime.util';
import { actionLinkClassName } from '@core/theme/actionTheme.util';
import Button from '@/components/Button';
import {
  buildMissingTimesheetSubmission,
  formatMissingShiftTimes,
  type MissingTimesheetTimes,
} from '@/modules/timesheets/utils/missingTimesheets.utils';
import { formatLoggedTime } from '@/utils/utils';

interface MissingTimesheetCardProps {
  item: MissingTimesheet;
  /** Edited times, or the scheduled shift when nothing has been edited. */
  times: MissingTimesheetTimes;
  canEdit: boolean;
  isEditing: boolean;
  didNotWork: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onToggleDidNotWork: () => void;
  onSave: (times: MissingTimesheetTimes) => void;
}

const fieldClassName =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-[var(--action-color)] focus:ring-1 focus:ring-[var(--action-color)] focus:outline-none';

const MissingTimesheetEditForm = ({
  item,
  times,
  onCancelEdit,
  onSave,
}: Pick<
  MissingTimesheetCardProps,
  'item' | 'times' | 'onCancelEdit' | 'onSave'
>) => {
  const [startTime, setStartTime] = useState(times.startTime);
  const [endTime, setEndTime] = useState(times.endTime);
  const [breakTimeMinutes, setBreakTimeMinutes] = useState(
    String(times.breakMinutes),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const breakMinutes = parseInt(breakTimeMinutes, 10) || 0;
  const preview = buildMissingTimesheetSubmission(
    item,
    startTime,
    endTime,
    breakMinutes,
  );
  const durationLabel = preview
    ? formatLoggedTime(
        preview.clockInTimestamp,
        preview.clockOutTimestamp,
        minutesToMilliseconds(breakMinutes),
      )
    : null;
  const isBreakTooLong = Boolean(preview) && durationLabel === '0 hrs';
  const canSave = Boolean(preview) && !isBreakTooLong;

  const handleSave = () => {
    if (!preview || isBreakTooLong) {
      setErrorMessage(
        isBreakTooLong
          ? 'Break time must be shorter than the shift.'
          : 'Enter a check-in and check-out time.',
      );
      return;
    }
    setErrorMessage(null);
    onSave({ startTime, endTime, breakMinutes });
  };

  return (
    <div className="mt-3 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm text-gray-500">
          Check-in time
          <input
            type="time"
            aria-label="Check-in time"
            value={startTime}
            onChange={(event) => setStartTime(event.target.value)}
            className={`${fieldClassName} mt-1`}
          />
        </label>
        <label className="block text-sm text-gray-500">
          Check-out time
          <input
            type="time"
            aria-label="Check-out time"
            value={endTime}
            onChange={(event) => setEndTime(event.target.value)}
            className={`${fieldClassName} mt-1`}
          />
        </label>
      </div>
      <label className="block text-sm text-gray-500">
        Break
        <span className="relative mt-1 block">
          <input
            type="number"
            min="0"
            step="1"
            aria-label="Break"
            value={breakTimeMinutes}
            onChange={(event) => {
              const value = event.target.value;
              if (value === '') {
                setBreakTimeMinutes('');
                return;
              }
              const minutes = parseInt(value, 10);
              if (!Number.isNaN(minutes) && minutes >= 0) {
                setBreakTimeMinutes(value);
              }
            }}
            onBlur={() => {
              if (breakTimeMinutes === '' || Number.isNaN(breakMinutes)) {
                setBreakTimeMinutes('0');
              }
            }}
            className={`${fieldClassName} pr-14`}
          />
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-500">
            mins
          </span>
        </span>
      </label>
      {(errorMessage || isBreakTooLong) && (
        <p role="alert" className="text-sm text-red-600">
          {errorMessage || 'Break time must be shorter than the shift.'}
        </p>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancelEdit}
          className="!rounded-lg !py-2 !text-sm !font-medium"
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={!canSave}
          onClick={handleSave}
          className="!rounded-lg !py-2 !text-sm !font-medium"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

const MissingTimesheetCard = ({
  item,
  times,
  canEdit,
  isEditing,
  didNotWork,
  onStartEdit,
  onCancelEdit,
  onToggleDidNotWork,
  onSave,
}: MissingTimesheetCardProps) => {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="shrink-0 text-sm font-medium whitespace-nowrap text-gray-900">
          {item.shiftDate ? dayjs(item.shiftDate).format('ddd, D MMM') : '—'}
        </p>
        {item.siteName && (
          <p className="flex max-w-2/3 min-w-0 items-start gap-1.5 text-sm text-gray-500">
            <FontAwesomeIcon
              icon={faLocationDot}
              className="mt-0.5 h-3.5 w-3.5 shrink-0"
            />
            <span className="min-w-0 text-left">{item.siteName}</span>
          </p>
        )}
      </div>
      <div className="mt-3 flex items-start justify-between gap-3">
        <Link
          to={`/jobs/${item.jobId}`}
          className={`cursor-pointer text-sm font-medium ${actionLinkClassName}`}
        >
          {item.jobTitle || 'View job'}
        </Link>
        {!isEditing && (
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
              didNotWork
                ? 'bg-gray-100 text-gray-600'
                : 'bg-red-50 text-red-600'
            }`}
          >
            {didNotWork ? 'Did not work' : 'Not submitted'}
          </span>
        )}
      </div>
      {isEditing ? (
        <MissingTimesheetEditForm
          item={item}
          times={times}
          onCancelEdit={onCancelEdit}
          onSave={onSave}
        />
      ) : (
        <>
          {!didNotWork && (
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Start/end time</span>
                <span className="text-gray-900">
                  {formatMissingShiftTimes(item, times)}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500">Break time</span>
                <span className="text-gray-900">{times.breakMinutes} mins</span>
              </div>
            </div>
          )}
          {canEdit && item.shiftDate && (
            <div className="mt-3 flex items-center justify-between gap-3">
              {!didNotWork && (
                <button
                  type="button"
                  onClick={onStartEdit}
                  className={`inline-flex cursor-pointer items-center gap-1.5 text-sm font-medium ${actionLinkClassName}`}
                >
                  <FontAwesomeIcon icon={faPen} className="h-3.5 w-3.5" />
                  Edit Timesheet
                </button>
              )}
              <label className="ml-auto inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                Did not work
                <input
                  type="checkbox"
                  checked={didNotWork}
                  onChange={onToggleDidNotWork}
                  className="h-4 w-4 cursor-pointer rounded border-gray-300"
                />
              </label>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MissingTimesheetCard;
